import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';

interface Deployment {
  id: string;
  project: string;
  environment: 'production' | 'staging' | 'development';
  status: 'success' | 'failed' | 'running' | 'pending';
  version: string;
  commit: string;
  branch: string;
  deployedBy: string;
  deployedAt: string;
  duration: number;
  url?: string;
  rollbackVersion?: string;
}

// What this environment's ECS service reports right now, as opposed to the
// deploy history above. rolloutState is the field that distinguishes a
// deployment still in progress from one the circuit breaker rolled back.
interface ServiceStatus {
  environment: string;
  service: string;
  desiredCount: number;
  runningCount: number;
  pendingCount: number;
  taskDefinition: string | null;
  image: string | null;
  deployedCommit: string | null;
  rolloutState: string | null;
  rolloutStateReason: string | null;
}

interface DeploymentMetrics {
  totalDeployments: number;
  successful: number;
  failed: number;
  running: number;
  averageDuration: number;
  lastDeployment: string;
}

const DeploymentsComponent: React.FC = () => {
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [metrics, setMetrics] = useState<DeploymentMetrics | null>(null);
  const [serviceStatus, setServiceStatus] = useState<ServiceStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>('all');

  useEffect(() => {
    fetchDeploymentData();

    // A deploy takes a few minutes and its status changes underneath us, so
    // the view refreshes rather than showing whatever was true on mount.
    const interval = setInterval(fetchDeploymentData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDeploymentData = async () => {
    setIsLoading(true);
    try {
      // Settled rather than all: the live ECS status comes from this
      // environment's own service and the history from GitHub Actions. Either
      // can be unavailable on its own, and one missing should not blank the
      // whole panel.
      const [deploymentsResult, metricsResult, statusResult] = await Promise.allSettled([
        apiService.getDeployments(),
        apiService.getDeploymentMetrics(),
        apiService.getDeploymentStatus(),
      ]);

      if (deploymentsResult.status === 'fulfilled') {
        setDeployments(deploymentsResult.value.data?.deployments ?? []);
        setError(null);
      } else {
        setError(deploymentsResult.reason?.message ?? 'Could not load deployments');
      }

      if (metricsResult.status === 'fulfilled') {
        setMetrics(metricsResult.value.data ?? null);
      }

      if (statusResult.status === 'fulfilled') {
        setServiceStatus(statusResult.value.data ?? null);
      } else {
        setServiceStatus(null);
      }
    } catch (err) {
      console.error('Error fetching deployment data:', err);
      setError(err instanceof Error ? err.message : 'Could not load deployment data');
    } finally {
      setIsLoading(false);
    }
  };

  const triggerDeployment = async (environment: string) => {
    // Production is a real release to real customers. The confirm is not
    // ceremony — this button is two clicks away from the order flow.
    if (environment === 'production') {
      const ok = window.confirm(
        'Request a production deploy?\n\n' +
        'This promotes the commit that staging most recently deployed successfully. ' +
        'It still has to be approved by a reviewer in GitHub before anything ships.',
      );
      if (!ok) return;
    }

    setBusy(true);
    setNotice(null);
    try {
      const response = await apiService.triggerDeployment(environment);
      setNotice(response.message ?? `Deployment to ${environment} requested`);
      await fetchDeploymentData();
    } catch (err) {
      console.error('Error triggering deployment:', err);
      setError(err instanceof Error ? err.message : 'Could not trigger the deployment');
    } finally {
      setBusy(false);
    }
  };

  const rollbackDeployment = async () => {
    const ok = window.confirm(
      'Roll back to the previous release?\n\n' +
      'This moves the running service back one task definition revision, immediately. ' +
      'It does not wait for a pipeline or an approval.',
    );
    if (!ok) return;

    setBusy(true);
    setNotice(null);
    try {
      const response = await apiService.rollbackDeployment();
      setNotice(response.message ?? 'Rollback started');
      await fetchDeploymentData();
    } catch (err) {
      console.error('Error rolling back deployment:', err);
      setError(err instanceof Error ? err.message : 'Could not roll back');
    } finally {
      setBusy(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'bg-scarf-green/20 text-scarf-green';
      case 'failed': return 'bg-brand-red/20 text-brand-red';
      case 'running': return 'bg-brand-blue-light/20 text-brand-blue-light';
      case 'pending': return 'bg-gray-100 text-gray-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getEnvironmentColor = (environment: string) => {
    switch (environment) {
      case 'production': return 'bg-red-100 text-red-700';
      case 'staging': return 'bg-brand-blue-light/20 text-brand-blue-light';
      case 'development': return 'bg-scarf-green/20 text-scarf-green';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const filteredDeployments = selectedEnvironment === 'all'
    ? deployments
    : deployments.filter(deployment => deployment.environment === selectedEnvironment);

  const environments = ['all', 'production', 'staging', 'development'];

  return (
    <div className="space-y-6">
      {/* Deployment Overview */}
      {metrics && (
        <div className="grid md:grid-cols-4 gap-6">
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Total Deployments</h3>
            <div className="text-3xl font-bold text-brand-blue">{metrics.totalDeployments}</div>
            <p className="text-gray-500 text-sm">All time</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Successful</h3>
            <div className="text-3xl font-bold text-scarf-green">{metrics.successful}</div>
            <p className="text-gray-500 text-sm">Last 30 days</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Failed</h3>
            <div className="text-3xl font-bold text-brand-red">{metrics.failed}</div>
            <p className="text-gray-500 text-sm">Last 30 days</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Avg Duration</h3>
            <div className="text-3xl font-bold text-brand-blue-light">{metrics.averageDuration}s</div>
            <p className="text-gray-500 text-sm">Per deployment</p>
          </div>
        </div>
      )}

      {/* Live state of this environment, straight from ECS. */}
      {serviceStatus && (
        <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-title font-bold text-deep-navy">
              Running now &mdash; {serviceStatus.environment}
            </h3>
            {serviceStatus.rolloutState && (
              <span className={`px-3 py-1 rounded text-xs font-bold ${
                serviceStatus.rolloutState === 'COMPLETED'
                  ? 'bg-scarf-green/20 text-scarf-green'
                  : serviceStatus.rolloutState === 'FAILED'
                    ? 'bg-brand-red/20 text-brand-red'
                    : 'bg-brand-blue-light/20 text-brand-blue-light'
              }`}>
                {serviceStatus.rolloutState}
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Tasks</p>
              <p className="font-bold text-deep-navy">
                {serviceStatus.runningCount} / {serviceStatus.desiredCount}
                {serviceStatus.pendingCount > 0 && ` (+${serviceStatus.pendingCount} starting)`}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Commit</p>
              <p className="font-bold text-deep-navy font-mono">
                {serviceStatus.deployedCommit?.slice(0, 7) ?? 'unknown'}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Task definition</p>
              <p className="font-bold text-deep-navy font-mono">{serviceStatus.taskDefinition ?? 'unknown'}</p>
            </div>
            <div>
              <p className="text-gray-500">Service</p>
              <p className="font-bold text-deep-navy">{serviceStatus.service}</p>
            </div>
          </div>

          {serviceStatus.rolloutState === 'FAILED' && serviceStatus.rolloutStateReason && (
            <p className="mt-4 text-sm text-brand-red">
              {serviceStatus.rolloutStateReason}
            </p>
          )}
        </div>
      )}

      {/* Quick Actions.
          One commit builds all three applications, so there is nothing to
          deploy per project any more — the axis that matters is which
          environment receives it. */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-2">Deployment Actions</h3>
        <p className="text-gray-500 text-sm mb-4">
          Staging deploys automatically when CI passes on main. Production promotes the
          commit staging last deployed successfully, and still waits for a reviewer in GitHub.
        </p>

        {notice && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-scarf-green/10 text-scarf-green text-sm font-bold">
            {notice}
          </div>
        )}

        {error && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-brand-red/10 text-brand-red text-sm font-bold">
            {error}
          </div>
        )}

        <div className="flex flex-wrap gap-4">
          <button
            onClick={() => triggerDeployment('staging')}
            disabled={busy}
            className="bg-brand-blue-light hover:bg-brand-blue disabled:opacity-50 text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            Deploy to staging
          </button>
          <button
            onClick={() => triggerDeployment('production')}
            disabled={busy}
            className="bg-brand-red hover:bg-brand-red/90 disabled:opacity-50 text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            Promote to production
          </button>
          <button
            onClick={rollbackDeployment}
            disabled={busy || !serviceStatus}
            className="bg-deep-navy hover:bg-deep-navy/90 disabled:opacity-50 text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            Roll back this environment
          </button>
          <button
            onClick={fetchDeploymentData}
            disabled={isLoading}
            className="bg-frost-white hover:bg-ice-teal-light disabled:opacity-50 text-deep-navy border border-ice-teal-light font-bold px-6 py-3 rounded-xl transition-colors"
          >
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Deployments Filter */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-title font-bold text-deep-navy">Deployment History</h3>
          <select
            value={selectedEnvironment}
            onChange={(e) => setSelectedEnvironment(e.target.value)}
            className="bg-frost-white border border-ice-teal-light rounded-lg px-4 py-2 text-gray-800 focus:outline-none focus:border-brand-blue"
          >
            {environments.map((env) => (
              <option key={env} value={env}>
                {env === 'all' ? 'All Environments' : env.charAt(0).toUpperCase() + env.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-gray-800">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Project</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Environment</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Status</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Version</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Branch</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Duration</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Deployed By</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Deployed At</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDeployments.map((deployment) => (
                <tr key={deployment.id} className="border-b border-gray-100">
                  <td className="py-3 px-4 font-bold">{deployment.project}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${getEnvironmentColor(deployment.environment)}`}>
                      {deployment.environment.toUpperCase()}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${getStatusColor(deployment.status)}`}>
                      {deployment.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 font-mono text-sm">{deployment.version}</td>
                  <td className="py-3 px-4 font-mono text-sm">{deployment.branch}</td>
                  <td className="py-3 px-4">{deployment.duration}s</td>
                  <td className="py-3 px-4 text-sm">{deployment.deployedBy}</td>
                  <td className="py-3 px-4 text-sm">
                    {new Date(deployment.deployedAt).toLocaleString()}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex space-x-2">
                      {deployment.url && (
                        <a
                          href={deployment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-2 py-1 bg-brand-blue-light hover:bg-brand-blue text-white rounded text-xs font-bold transition-colors"
                        >
                          View
                        </a>
                      )}

                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Deployment Statistics */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Deployment Statistics</h3>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="text-center">
            <h4 className="text-deep-navy font-bold mb-2">Success Rate</h4>
            <div className="text-3xl font-bold text-scarf-green">
              {metrics ? Math.round((metrics.successful / metrics.totalDeployments) * 100) : 0}%
            </div>
            <p className="text-gray-500 text-sm">Last 30 days</p>
          </div>
          <div className="text-center">
            <h4 className="text-deep-navy font-bold mb-2">Average Duration</h4>
            <div className="text-3xl font-bold text-brand-blue-light">
              {metrics ? metrics.averageDuration : 0}s
            </div>
            <p className="text-gray-500 text-sm">Per deployment</p>
          </div>
          <div className="text-center">
            <h4 className="text-deep-navy font-bold mb-2">Last Deployment</h4>
            <div className="text-lg font-bold text-brand-red">
              {metrics ? new Date(metrics.lastDeployment).toLocaleDateString() : 'N/A'}
            </div>
            <p className="text-gray-500 text-sm">Production</p>
          </div>
        </div>
      </div>

      {/* Deployment Best Practices */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Deployment Best Practices</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-scarf-green font-bold mb-3">Before Deployment</h4>
            <ul className="text-gray-600 space-y-2">
              <li>Run all tests successfully</li>
              <li>Review code changes</li>
              <li>Check environment configuration</li>
              <li>Verify database migrations</li>
            </ul>
          </div>
          <div>
            <h4 className="text-scarf-green font-bold mb-3">After Deployment</h4>
            <ul className="text-gray-600 space-y-2">
              <li>Monitor application health</li>
              <li>Check error logs</li>
              <li>Verify functionality</li>
              <li>Update documentation</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeploymentsComponent;
